/**
 * PriceChangeBanner Component
 *
 * MVP 1.22 - Experience Pricing & Promotions Engine
 * Story: SCRUM-375 - Proceed to Booking Flow
 * Story: SCRUM-376 - Handle Price Change During Booking
 *
 * Displays price change notifications during booking flow:
 * - AC-004.4: Price decrease = positive notification
 * - AC-004.5: Price increase = warning and acknowledgment
 * - AC-005.1: Price increase banner shows original vs. new price
 * - AC-005.2: Percentage and dollar amount both displayed
 * - AC-005.5: Reason for price change explained
 * - AC-005.6: Price decrease shows celebratory messaging
 */

import React from 'react';
import PropTypes from 'prop-types';
import { formatCentsToDollars } from '../../utils/formHelpers';

/**
 * Price Decreased Banner (AC-005.6)
 */
const DecreasedBanner = ({ originalCents, freshCents, differencesCents, reasons, onContinue }) => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0">
        <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-green-800">
          Good news! The price has dropped.
        </h3>
        <div className="mt-2 text-sm text-green-700">
          <p>
            <span className="line-through text-green-600">{formatCentsToDollars(originalCents)}</span>
            {' '}
            <span className="font-semibold">{formatCentsToDollars(freshCents)}</span>
            <span className="ml-2 text-green-600">
              (Save {formatCentsToDollars(Math.abs(differencesCents))})
            </span>
          </p>
          {reasons && reasons.length > 0 && (
            <p className="mt-1 text-green-600">{reasons[0]}</p>
          )}
        </div>
        {onContinue && (
          <button
            type="button"
            onClick={onContinue}
            className="mt-3 text-sm font-medium text-green-700 hover:text-green-800"
          >
            Continue with new price →
          </button>
        )}
      </div>
    </div>
  </div>
);

DecreasedBanner.propTypes = {
  originalCents: PropTypes.number.isRequired,
  freshCents: PropTypes.number.isRequired,
  differencesCents: PropTypes.number.isRequired,
  reasons: PropTypes.arrayOf(PropTypes.string),
  onContinue: PropTypes.func
};

/**
 * Price Increased Banner (AC-005.1, AC-005.2, AC-005.5)
 */
const IncreasedBanner = ({
  originalCents,
  freshCents,
  differencesCents,
  differencePercent,
  reasons,
  onGoBack,
  onContinue
}) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0">
        <svg className="w-6 h-6 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-amber-800">
          Price has changed
        </h3>
        <div className="mt-2 text-sm text-amber-700">
          {/* AC-005.1: Original vs new price clearly shown */}
          <div className="flex items-baseline gap-2">
            <span className="line-through text-amber-600">{formatCentsToDollars(originalCents)}</span>
            <span className="text-lg font-semibold text-amber-900">{formatCentsToDollars(freshCents)}</span>
          </div>
          {/* AC-005.2: Percentage AND dollar amount */}
          <p className="mt-1 text-amber-600">
            +{formatCentsToDollars(differencesCents)} ({differencePercent > 0 ? '+' : ''}{differencePercent}%)
          </p>
          {/* AC-005.5: Reason for price change */}
          {reasons && reasons.length > 0 && (
            <div className="mt-2 text-amber-600">
              <p className="font-medium">Why did the price change?</p>
              <ul className="list-disc list-inside mt-1">
                {reasons.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {/* AC-005.3, AC-005.4: Go Back and Continue buttons */}
        <div className="mt-4 flex gap-3">
          {onGoBack && (
            <button
              type="button"
              onClick={onGoBack}
              className="px-4 py-2 text-sm font-medium text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50"
            >
              Go Back
            </button>
          )}
          {onContinue && (
            <button
              type="button"
              onClick={onContinue}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
            >
              Continue at {formatCentsToDollars(freshCents)}
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

IncreasedBanner.propTypes = {
  originalCents: PropTypes.number.isRequired,
  freshCents: PropTypes.number.isRequired,
  differencesCents: PropTypes.number.isRequired,
  differencePercent: PropTypes.number.isRequired,
  reasons: PropTypes.arrayOf(PropTypes.string),
  onGoBack: PropTypes.func,
  onContinue: PropTypes.func
};

/**
 * Quote Expired Banner (AC-004.6)
 */
const ExpiredBanner = ({ originalCents, onRefresh }) => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0">
        <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-gray-800">
          Your quote has expired
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Pricing quotes are valid for 15 minutes. Your quoted price was {formatCentsToDollars(originalCents)}.
        </p>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            className="mt-3 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
          >
            Get Updated Price
          </button>
        )}
      </div>
    </div>
  </div>
);

ExpiredBanner.propTypes = {
  originalCents: PropTypes.number,
  onRefresh: PropTypes.func
};

/**
 * PriceChangeBanner Component
 *
 * Main component that renders the appropriate banner based on comparison status.
 */
const PriceChangeBanner = ({
  comparison,
  originalTotalCents,
  freshTotalCents,
  error,
  onGoBack,
  onContinue,
  onRefresh,
  className = ''
}) => {
  // Handle error states
  if (error === 'QUOTE_EXPIRED') {
    return (
      <div className={className}>
        <ExpiredBanner
          originalCents={originalTotalCents}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  if (error === 'QUOTE_NOT_FOUND') {
    return (
      <div className={className}>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Unable to verify pricing. Please try again.
        </div>
      </div>
    );
  }

  // No comparison data = no banner needed
  if (!comparison) {
    return null;
  }

  const { status, differencesCents, differencePercent, reasons } = comparison;

  // Price match = no banner needed (AC-004.3)
  if (status === 'match') {
    return null;
  }

  return (
    <div className={className}>
      {status === 'decreased' && (
        <DecreasedBanner
          originalCents={originalTotalCents}
          freshCents={freshTotalCents}
          differencesCents={differencesCents}
          reasons={reasons}
          onContinue={onContinue}
        />
      )}
      {status === 'increased' && (
        <IncreasedBanner
          originalCents={originalTotalCents}
          freshCents={freshTotalCents}
          differencesCents={differencesCents}
          differencePercent={differencePercent}
          reasons={reasons}
          onGoBack={onGoBack}
          onContinue={onContinue}
        />
      )}
    </div>
  );
};

PriceChangeBanner.propTypes = {
  comparison: PropTypes.shape({
    status: PropTypes.oneOf(['match', 'decreased', 'increased']).isRequired,
    differencesCents: PropTypes.number.isRequired,
    differencePercent: PropTypes.number.isRequired,
    reasons: PropTypes.arrayOf(PropTypes.string)
  }),
  originalTotalCents: PropTypes.number,
  freshTotalCents: PropTypes.number,
  error: PropTypes.string,
  onGoBack: PropTypes.func,
  onContinue: PropTypes.func,
  onRefresh: PropTypes.func,
  className: PropTypes.string
};

export default PriceChangeBanner;
